import { TableChart } from '@mui/icons-material';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useState } from 'react';

interface TableInsertDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onInsert: (rows: number, cols: number, withHeaderRow: boolean) => void;
}

export function TableInsertDialog({ open, onOpenChange, onInsert }: TableInsertDialogProps) {
    const [rows, setRows] = useState(3);
    const [cols, setCols] = useState(3);
    const [withHeaderRow, setWithHeaderRow] = useState(true);

    const handleInsert = () => {
        if (rows > 0 && rows <= 20 && cols > 0 && cols <= 10) {
            onInsert(rows, cols, withHeaderRow);
            onOpenChange(false);
            setRows(3);
            setCols(3);
            setWithHeaderRow(true);
        }
    };

    const handleRowsChange = (value: string) => {
        const num = parseInt(value, 10);
        if (!isNaN(num) && num >= 1 && num <= 20) {
            setRows(num);
        }
    };

    const handleColsChange = (value: string) => {
        const num = parseInt(value, 10);
        if (!isNaN(num) && num >= 1 && num <= 10) {
            setCols(num);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={() => onOpenChange(false)}
            fullWidth
            maxWidth="xs"
            sx={{ zIndex: 10000 }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TableChart sx={{ fontSize: 22 }} />
                Insert Table
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Specify the number of rows and columns for your table.
                </Typography>
                <Stack direction="row" spacing={2}>
                    <TextField
                        id="rows"
                        label="Rows"
                        type="number"
                        size="small"
                        fullWidth
                        value={rows}
                        onChange={(event) => handleRowsChange(event.target.value)}
                        helperText="Max: 20 rows"
                        slotProps={{ htmlInput: { min: 1, max: 20 } }}
                    />
                    <TextField
                        id="cols"
                        label="Columns"
                        type="number"
                        size="small"
                        fullWidth
                        value={cols}
                        onChange={(event) => handleColsChange(event.target.value)}
                        helperText="Max: 10 columns"
                        slotProps={{ htmlInput: { min: 1, max: 10 } }}
                    />
                </Stack>
                <FormControlLabel
                    sx={{ mt: 1 }}
                    control={
                        <Checkbox
                            id="headerRow"
                            checked={withHeaderRow}
                            onChange={(event) => setWithHeaderRow(event.target.checked)}
                        />
                    }
                    label="Include header row"
                />
                <Box
                    sx={{
                        mt: 1.5,
                        p: 2,
                        borderRadius: 1,
                        border: 1,
                        borderColor: 'divider',
                        bgcolor: 'grey.50',
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Preview:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {rows} rows × {cols} columns
                        {withHeaderRow ? ' (with header)' : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {withHeaderRow ? `1 header row + ${rows - 1} data rows` : 'All data rows'}
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    variant="outlined"
                    onClick={() => onOpenChange(false)}
                    sx={{ textTransform: 'none' }}
                >
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleInsert} sx={{ textTransform: 'none' }}>
                    Insert Table
                </Button>
            </DialogActions>
        </Dialog>
    );
}
